import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, QrCode, ClipboardList, ListChecks, Bot, Library,
  ShoppingBag, BarChart3, GraduationCap, Leaf, ShieldCheck, HardHat,
  Building2, Users, MapPin, Moon, Sun, LogOut, Menu, X, Bell, Store, UserCog, HeartPulse, Inbox, Network,
} from "lucide-react";
import { Logo } from "./shared";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";

const GROUPS = [
  { items: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "client_admin", "manager"], end: true },
    { to: "/app/group", label: "Group View", icon: Network, roles: ["client_admin", "manager"] },
    { to: "/app/health", label: "Health Check", icon: HeartPulse, roles: ["client_admin", "manager"] },
    { to: "/app/report", label: "Report Issue", icon: QrCode, roles: ["employee", "manager", "client_admin"] },
    { to: "/app/reports", label: "Reports", icon: ClipboardList, roles: ["client_admin", "manager"] },
    { to: "/app/actions", label: "Actions", icon: ListChecks, roles: ["client_admin", "manager"] },
    { to: "/app/assistant", label: "AI Assistant", icon: Bot, roles: ["client_admin", "manager"] },
    { to: "/app/resources", label: "Resource Library", icon: Library, roles: ["client_admin", "manager", "employee"] },
  ]},
  { section: "Compliance", items: [
    { to: "/app/iso", label: "ISO Compliance", icon: ShieldCheck, roles: ["client_admin", "manager"] },
    { to: "/app/training", label: "Training", icon: GraduationCap, roles: ["client_admin", "manager"] },
    { to: "/app/esg", label: "ESG", icon: Leaf, roles: ["client_admin", "manager"] },
    { to: "/app/contractors", label: "Contractors", icon: HardHat, roles: ["client_admin", "manager"] },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, roles: ["client_admin", "manager"] },
  ]},
  { section: "Organisation", items: [
    { to: "/app/marketplace", label: "Marketplace", icon: ShoppingBag, roles: ["client_admin"] },
    { to: "/app/sites", label: "Sites & QR", icon: MapPin, roles: ["client_admin", "manager"] },
    { to: "/app/team", label: "Team", icon: Users, roles: ["client_admin", "manager"] },
  ]},
  { section: "SEQ / Amden Admin", items: [
    { to: "/app/clients", label: "Clients", icon: Building2, roles: ["super_admin"] },
    { to: "/app/super-users", label: "Super Users", icon: UserCog, roles: ["super_admin"] },
    { to: "/app/catalog", label: "Marketplace Catalog", icon: Store, roles: ["super_admin"] },
    { to: "/app/requests", label: "Service Requests", icon: Inbox, roles: ["super_admin"] },
  ]},
];

function NavItems({ role, onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {GROUPS.map((group, gi) => {
        const visible = group.items.filter((it) => it.roles.includes(role));
        if (visible.length === 0) return null;
        return (
          <div key={gi}>
            {group.section && (
              <div className="px-3 pb-1 pt-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{group.section}</div>
            )}
            {visible.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive ? "bg-secondary text-[hsl(var(--accent))]" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                    }`
                  }>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={2} /> {item.label}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}

export function Sidebar() {
  const { effectiveRole } = useAuth();
  return (
    <aside className="hidden w-[270px] shrink-0 flex-col border-r border-border bg-card lg:flex">
      <div className="flex h-16 items-center border-b border-border px-5"><Logo /></div>
      <div className="flex-1 overflow-y-auto py-3 no-scrollbar"><NavItems role={effectiveRole} /></div>
    </aside>
  );
}

export function Topbar({ onMenu }) {
  const { user, org, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const roleLabel = { super_admin: "Super Admin", client_admin: "Client Admin", manager: "Manager", employee: "Employee" }[user?.role];
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-xl lg:px-8">
      <div className="flex items-center gap-3">
        <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <div>
          <div className="font-heading text-sm font-semibold tracking-tight">{org?.name || "Foresee Platform"}</div>
          <div className="text-xs text-muted-foreground">{roleLabel}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button variant="ghost" size="icon" className="rounded-full" data-testid="notifications-btn"><Bell className="h-[18px] w-[18px]" /></Button>
        <Button variant="ghost" size="icon" className="rounded-full" onClick={toggle} data-testid="theme-toggle">
          {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </Button>
        <div className="mx-1 hidden items-center gap-2.5 rounded-full border border-border py-1 pl-1 pr-3 sm:flex">
          <Avatar className="h-7 w-7"><AvatarFallback className="bg-[hsl(var(--accent))] text-xs text-white">{user?.name?.[0]}</AvatarFallback></Avatar>
          <div className="text-xs font-medium leading-none">{user?.name}</div>
        </div>
        <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground" onClick={logout} data-testid="logout-btn"><LogOut className="h-[18px] w-[18px]" /></Button>
      </div>
    </header>
  );
}

export function MobileNav({ open, onClose }) {
  const { effectiveRole } = useAuth();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ x: -300 }} animate={{ x: 0 }} className="absolute left-0 top-0 h-full w-[280px] overflow-y-auto bg-card no-scrollbar">
        <div className="flex h-16 items-center justify-between border-b border-border px-5"><Logo /><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="py-3"><NavItems role={effectiveRole} onNavigate={onClose} /></div>
      </motion.div>
    </div>
  );
}
